/**
 * Makes, models and trims as the workspace offers them in drop-downs (business review 2026-09-23, plan
 * §11.19). There is no catalogue table: the makes and models are the distinct ones the templates already
 * carry, and the trim is one of three grades. Stored values stay plain strings, so an older free-text
 * value is shown as an extra option instead of disappearing.
 */
export const TRIMS = ['High', 'Mid', 'Low'] as const;

export type Trim = (typeof TRIMS)[number];

const distinct = (values: readonly (string | null | undefined)[]): string[] => {
  const seen = new Map<string, string>();

  for (const value of values) {
    const trimmed = value?.trim() ?? '';

    if (trimmed !== '' && !seen.has(trimmed.toLowerCase())) {
      seen.set(trimmed.toLowerCase(), trimmed);
    }
  }

  return [...seen.values()].sort((left, right) => left.localeCompare(right));
};

export interface CatalogSource {
  make: string;
  model: string;
}

export const makesOf = (templates: readonly CatalogSource[]): string[] => distinct(templates.map((template) => template.make));

/** The models of one make; every model when no make is chosen yet. */
export const modelsOf = (templates: readonly CatalogSource[], make: string): string[] =>
  distinct(
    templates
      .filter((template) => make.trim() === '' || template.make.trim().toLowerCase() === make.trim().toLowerCase())
      .map((template) => template.model),
  );

/** The three grades, plus a legacy value the record already carries so it still displays. */
export const trimOptions = (current: string): string[] => {
  const trimmed = current.trim();

  return trimmed === '' || TRIMS.some((trim) => trim.toLowerCase() === trimmed.toLowerCase()) ? [...TRIMS] : [...TRIMS, trimmed];
};
